import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AppPage } from '@/components/layout/AppPage';
import { useViewportSize } from '@/hooks/useViewportSize';
import { toast } from 'sonner';
import {
  Search,
  Lock,
  ArrowRight,
  X,
  ShieldCheck,
  ExternalLink,
  LayoutList,
  BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['All', 'Contracts', 'Court Filings', 'Notices', 'Property', 'Corporate', 'Student'];

const SOURCE_STATUS_META = {
  PRESCRIBED_FORM: {
    label: 'Prescribed Form',
    accent: '#4ade80',
    glow: 'rgba(74, 222, 128, 0.16)',
  },
  COURT_GUIDED: {
    label: 'Court Guided',
    accent: '#60a5fa',
    glow: 'rgba(96, 165, 250, 0.16)',
  },
  STATUTE_GUIDED: {
    label: 'Statute Guided',
    accent: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.16)',
  },
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { width, height } = useViewportSize();
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [templatePage, setTemplatePage] = useState(0);
  const [landingExpanded, setLandingExpanded] = useState(false);

  useEffect(() => {
    fetchTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const resp = await fetch(`${API}/templates`, { headers: getAuthHeaders(), credentials: 'include' });
      if (resp.ok) setTemplates(await resp.json());
    } catch {}
  };

  const applyTemplate = async (template) => {
    setCreating(true);
    try {
      const resp = await fetch(`${API}/documents`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          title: template.title,
          type: template.type,
          content: '',
          parent_template_id: template.template_id,
        }),
      });
      if (!resp.ok) throw new Error('create failed');
      const doc = await resp.json();
      toast.success('Template workspace ready');
      navigate(`/documents/${doc.doc_id}`);
    } catch {
      toast.error('Failed to create document');
    } finally {
      setCreating(false);
    }
  };

  const filtered = templates.filter(template => {
    if (searchQuery && !template.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (category !== 'All' && template.category !== category) return false;
    return true;
  });
  const premiumCount = templates.filter(template => template.is_premium).length;
  const guidedCount = templates.filter(template => template.source_status === 'COURT_GUIDED' || template.source_status === 'PRESCRIBED_FORM').length;
  const isCompactViewport = width < 1180 || height < 900;
  const isLandingCollapsed = !landingExpanded && !searchQuery.trim() && category === 'All' && !templatesExpanded && templatePage === 0 && !selectedTemplate;
  const initialVisibleTemplates = filtered.slice(0, isCompactViewport ? 2 : 4);
  const pagedTemplates = filtered.slice(templatePage * 10, templatePage * 10 + 10);
  const visibleTemplates = isLandingCollapsed ? initialVisibleTemplates : (templatesExpanded ? pagedTemplates : filtered);
  const totalTemplatePages = Math.max(1, Math.ceil(filtered.length / 10));
  const hiddenTemplateCount = Math.max(0, filtered.length - visibleTemplates.length);

  const handleExpandTemplates = () => {
    setLandingExpanded(true);
    setTemplatesExpanded(true);
    setTemplatePage(0);
  };

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="templates-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AppPage className={`app-page--landing-locked ${isLandingCollapsed ? 'app-page--landing-collapsed' : 'app-page--landing-expanded'}`}>
          <div className="advoq-card p-4 mb-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {templates.length} template library
                </span>
                <span className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {guidedCount} guided sources
                </span>
                <span className="text-xs px-3 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {premiumCount} premium templates
                </span>
              </div>

              <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  data-testid="templates-search"
                  value={searchQuery}
                  onChange={event => {
                    setSearchQuery(event.target.value);
                    setLandingExpanded(true);
                    setTemplatePage(0);
                  }}
                  className="advoq-input w-full pl-10 text-sm"
                placeholder="Search templates..."
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {CATEGORIES.map(item => (
                <button
                  key={item}
                  onClick={() => {
                    setCategory(item);
                    setLandingExpanded(true);
                    setTemplatePage(0);
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150"
                  style={{
                    background: category === item ? 'var(--accent-glow)' : 'transparent',
                    color: category === item ? 'var(--accent-primary)' : 'var(--text-muted)',
                    border: `1px solid ${category === item ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                Structured starting points for serious drafting
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                {isLandingCollapsed
                  ? 'Landing stays compact first. Expand only when you want the full precedent desk.'
                  : templatesExpanded
                  ? `Showing page ${templatePage + 1} of ${totalTemplatePages}`
                  : 'Showing the full filtered precedent list.'}
              </p>
            </div>
            {isLandingCollapsed && hiddenTemplateCount > 0 && (
              <button type="button" className="advoq-btn-secondary text-sm" onClick={handleExpandTemplates}>
                Open full desk
              </button>
            )}
          </div>

          {isLandingCollapsed && hiddenTemplateCount > 0 && (
            <div className="landing-collapsed-banner mb-4">
              <p className="landing-collapsed-banner__copy">
                <strong>Compact first pass.</strong> Showing the templates that fit on landing before the page becomes scrollable.
              </p>
              <button type="button" className="advoq-btn-secondary text-sm" onClick={handleExpandTemplates}>
                Show {hiddenTemplateCount} more template{hiddenTemplateCount === 1 ? '' : 's'}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-4">
            {visibleTemplates.map(template => {
              const statusMeta = SOURCE_STATUS_META[template.source_status] || SOURCE_STATUS_META.STATUTE_GUIDED;
              const sections = template.editor_schema?.sections || [];
              const references = template.official_references || [];

              return (
                <button
                  key={template.template_id}
                  type="button"
                  onClick={() => {
                    setLandingExpanded(true);
                    setSelectedTemplate(template);
                  }}
                  className="advoq-card p-5 text-left relative overflow-hidden"
                  data-testid={`template-${template.template_id}`}
                >
                  <div
                    className="absolute inset-x-0 top-0 h-1"
                    style={{ background: `linear-gradient(90deg, ${statusMeta.accent}, transparent)` }}
                  />
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium"
                        style={{ background: statusMeta.glow, color: statusMeta.accent }}
                      >
                        <ShieldCheck size={12} />
                        {statusMeta.label}
                      </span>
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
                        style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                      >
                        {template.category}
                      </span>
                    </div>
                    {template.is_premium && user?.plan === 'FREE' && (
                      <Lock size={14} style={{ color: 'var(--warning-amber)' }} />
                    )}
                  </div>

                  <h3 className="text-lg font-semibold mb-2 leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
                    {template.title}
                  </h3>
                  <p className="text-sm leading-6 mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {template.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-2xl px-3 py-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <LayoutList size={13} style={{ color: 'var(--accent-primary)' }} />
                        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                          Sections
                        </span>
                      </div>
                      <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{sections.length}</p>
                    </div>
                    <div className="rounded-2xl px-3 py-3" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <BookOpen size={13} style={{ color: 'var(--accent-primary)' }} />
                        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                          Sources
                        </span>
                      </div>
                      <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{references.length}</p>
                    </div>
                  </div>

                  <p className="text-xs leading-5 mb-4" style={{ color: 'var(--text-muted)' }}>
                    {template.source_note}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {template.usage_count || 0} workspaces created
                    </span>
                    <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--accent-primary)' }}>
                      Preview
                      <ArrowRight size={12} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {templatesExpanded && filtered.length > 10 && (
            <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
              <button
                type="button"
                className="advoq-btn-secondary text-sm"
                disabled={templatePage === 0}
                onClick={() => setTemplatePage(prev => Math.max(0, prev - 1))}
              >
                Previous
              </button>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Templates {(templatePage * 10) + 1}-{Math.min((templatePage + 1) * 10, filtered.length)} of {filtered.length}
              </span>
              <button
                type="button"
                className="advoq-btn-secondary text-sm"
                disabled={templatePage >= totalTemplatePages - 1}
                onClick={() => setTemplatePage(prev => Math.min(totalTemplatePages - 1, prev + 1))}
              >
                Next
              </button>
            </div>
          )}
        </AppPage>

        {selectedTemplate && (
          <div className="app-modal-backdrop">
            <div
              className="w-full max-w-6xl mx-4 max-h-[88vh] flex flex-col rounded-[28px] overflow-hidden"
              style={{ background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-lg)' }}
              data-testid="template-preview-modal"
            >
              <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                <div>
                  <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>{selectedTemplate.title}</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{selectedTemplate.description}</p>
                </div>
                <button onClick={() => setSelectedTemplate(null)} className="p-1.5 rounded" style={{ color: 'var(--text-muted)' }}>
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="grid xl:grid-cols-[1.1fr,0.9fr] gap-0">
                  <div className="p-6 sm:p-7 border-b xl:border-b-0 xl:border-r" style={{ borderColor: 'var(--border-subtle)' }}>
                    <div
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-medium mb-4"
                      style={{
                        background: SOURCE_STATUS_META[selectedTemplate.source_status]?.glow || SOURCE_STATUS_META.STATUTE_GUIDED.glow,
                        color: SOURCE_STATUS_META[selectedTemplate.source_status]?.accent || SOURCE_STATUS_META.STATUTE_GUIDED.accent,
                      }}
                    >
                      <ShieldCheck size={12} />
                      {SOURCE_STATUS_META[selectedTemplate.source_status]?.label || 'Structured Template'}
                    </div>

                    <p className="text-sm leading-7 mb-5" style={{ color: 'var(--text-secondary)' }}>
                      {selectedTemplate.source_note}
                    </p>

                    <div className="grid gap-4">
                      {(selectedTemplate.official_references || []).map((ref, index) => (
                        <a
                          key={`${ref.url}-${index}`}
                          href={ref.url}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-[22px] p-4 block"
                          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{ref.title}</p>
                              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{ref.authority}</p>
                            </div>
                            <ExternalLink size={14} style={{ color: 'var(--text-muted)' }} />
                          </div>
                          <p className="text-xs leading-6 mt-3" style={{ color: 'var(--text-muted)' }}>{ref.relevance}</p>
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="p-6 sm:p-7">
                    <div className="flex items-center gap-2 mb-4">
                      <LayoutList size={14} style={{ color: 'var(--accent-primary)' }} />
                      <h4 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
                        Drafting Structure
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {(selectedTemplate.editor_schema?.sections || []).map((section, index) => (
                        <div
                          key={`${section.id}-${index}`}
                          className="rounded-[20px] p-4"
                          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                        >
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <span className="text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                              Section {index + 1}
                            </span>
                            {section.document_heading && (
                              <span
                                className="text-[10px] px-2 py-1 rounded-full"
                                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                              >
                                H{section.level || 2}
                              </span>
                            )}
                          </div>
                          <h5 className="text-sm font-semibold leading-6" style={{ color: 'var(--text-primary)' }}>
                            {section.document_heading || section.editor_label}
                          </h5>
                          <p className="text-xs leading-6 mt-2" style={{ color: 'var(--text-secondary)' }}>
                            {section.guidance || 'Draft within this block while keeping the section order intact.'}
                          </p>
                          {(section.placeholders || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {section.placeholders.map(token => (
                                <span
                                  key={token}
                                  className="rounded-full px-2.5 py-1 text-[11px]"
                                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                                >
                                  [{token}]
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 flex gap-3 shrink-0" style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                <button onClick={() => setSelectedTemplate(null)} className="advoq-btn-ghost flex-1 text-sm">Close</button>
                <button
                  data-testid="use-template-confirm"
                  onClick={() => applyTemplate(selectedTemplate)}
                  disabled={creating}
                  className="advoq-btn-primary flex-1 text-sm"
                >
                  {creating ? 'Creating...' : 'Start Draft Workspace'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
